import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  Image,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  type RegistrationSuccessInfo,
  type RegistrationVerificationState,
  getRegistrationVerificationPresentation,
  REGISTRATION_VERIFICATION_BADGE_STYLES,
} from '@diamond/shared/features/registration/browser';
import { getPublicHomepageOrigin } from '@diamond/sol-buyer/utils';
import { useAuthentication, useAuthStore } from '@diamond/sol-buyer-context';
import {
  ArrowCircleRightOutlined,
  KeyboardArrowDownOutlined,
  LogoutOutlined,
  NotificationsOutlined,
  PersonOutlined,
} from '@mui/icons-material';
import NextLink from 'next/link';

import {
  NOO_NAV_GRADIENT,
  WHATSAPP_SUPPORT_URL,
} from './NooRegistrationStatusConstants';
import {
  BellIcon,
  ClockIcon,
  NooLogoGram,
  ShoppingBagIcon,
  StatusBadgeIcon,
  SuccessHomeIcon,
} from './NooRegistrationStatusIcons';

export function StatusBadge({
  state,
}: {
  state: RegistrationVerificationState;
}) {
  const presentation = getRegistrationVerificationPresentation(state);
  const styles = REGISTRATION_VERIFICATION_BADGE_STYLES[state];

  return (
    <HStack
      spacing={2}
      px={3}
      py={1}
      borderRadius="full"
      bg={styles.bg}
      color={styles.color}
      fontSize="12px"
      lineHeight="16px"
      fontWeight="normal"
      align="center"
    >
      <StatusBadgeIcon state={state} />
      <Text as="span">{presentation.badgeLabel}</Text>
    </HStack>
  );
}
export function NooStatusPageHeader() {
  const accessToken = useAuthStore((state) => state.access_token);
  const { logout } = useAuthentication();

  const handleLogout = () => {
    void logout(accessToken ?? '');
  };

  return (
    <Box
      as="header"
      position={{ base: 'sticky', lg: 'static' }}
      top={0}
      zIndex={10}
      bg={{ base: '#FFFFFF', lg: '#00003D' }}
      boxShadow={{ base: '0 1px 4px #0000001A', lg: 'none' }}
      data-testid="noo-status-page-header"
    >
      <Box
        display={{ base: 'block', lg: 'none' }}
        pt="calc(1.5rem + env(safe-area-inset-top, 0px))"
      >
        <Container maxW="390px" px={4} pb={4}>
          <Flex align="center" gap={2}>
            <NooLogoGram />
            <Text
              flex={1}
              fontFamily="Ubuntu, sans-serif"
              fontSize="20px"
              fontWeight="medium"
              lineHeight="32px"
              letterSpacing="0.2px"
              color="#373F41"
            >
              Detail Pengajuan Rekanan
            </Text>
            <BellIcon />
          </Flex>
        </Container>
        <Box h="4px" bgGradient={NOO_NAV_GRADIENT} />
      </Box>

      <Box
        display={{ base: 'none', lg: 'block' }}
        bg="#00003D"
        px="72px"
        borderBottom="1px solid"
        borderColor="#E9EDF3"
      >
        <Flex h="88px" align="center" justify="space-between">
          <Box as={NextLink} href="/" aria-label="Sukanda OneLink">
            <Image
              src="/images/sol_desktop_logo.png"
              alt="Sukanda OneLink"
              width="155px"
              height="61px"
              objectFit="contain"
            />
          </Box>

          <HStack spacing={5} color="#FFFFFF">
            <Button
              as={NextLink}
              href="/notification?page=1"
              variant="ghost"
              minW="40px"
              h="40px"
              px={2}
              borderRadius="8px"
              bg="#F2F2F2"
              color="#00003D"
              _hover={{ bg: '#E5E7EB' }}
              aria-label="Notifikasi"
            >
              <Icon as={NotificationsOutlined} boxSize={6} />
            </Button>
            <Box h="32px" borderLeft="1px solid" borderColor="#FFFFFF80" />
            <Popover placement="bottom-end">
              <PopoverTrigger>
                <Button
                  variant="ghost"
                  h="40px"
                  minW="40px"
                  px={2}
                  borderRadius="8px"
                  bg="#F2F2F2"
                  color="#00003D"
                  _hover={{ bg: '#E5E7EB' }}
                  aria-label="Akun"
                  aria-haspopup="menu"
                  data-testid="noo-status-account-menu-trigger"
                >
                  <Icon as={PersonOutlined} boxSize={6} />
                  <Icon as={KeyboardArrowDownOutlined} boxSize={5} />
                </Button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="auto" minW="220px" borderRadius="8px">
                  <PopoverBody p={2}>
                    <Button
                      w="full"
                      h="48px"
                      bgColor="#DC2626"
                      color="#FFFFFF"
                      fontSize="sm"
                      fontWeight="medium"
                      borderRadius="8px"
                      _hover={{ bgColor: '#B91C1C' }}
                      _active={{ bgColor: '#991B1B' }}
                      rightIcon={<Icon as={LogoutOutlined} boxSize={5} />}
                      onClick={handleLogout}
                      data-testid="noo-status-logout"
                    >
                      Keluar
                    </Button>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
}
export function PendingReviewBanner() {
  return (
    <Box
      border="1px solid"
      borderColor="#F59E0B33"
      bg="#FFFBEB"
      borderRadius="10px"
      px={6}
      pt={4}
      pb={6}
      data-testid="noo-buyer-pending-review-banner"
    >
      <HStack align="start" spacing={3}>
        <ClockIcon />
        <Box>
          <Text
            fontSize="xl"
            fontWeight="medium"
            lineHeight="28px"
            color="#7B3306"
          >
            Pengajuan Diproses
          </Text>
          <Text mt={2} fontSize="sm" lineHeight="20px" color="#BB4D00">
            Dokumen Pengajuan sedang diverifikasi. Estimasi waktu: 5-7 hari
            kerja.
          </Text>
        </Box>
      </HStack>
    </Box>
  );
}
function SuccessDetailRow({
  label,
  value,
  isPlaceholder = false,
}: {
  label: string;
  value: string;
  isPlaceholder?: boolean;
}) {
  return (
    <Flex fontSize="sm" lineHeight="20px" color="#008236">
      <Text>{label}</Text>
      <Text
        ml={2}
        fontSize="md"
        fontWeight={isPlaceholder ? 'medium' : 'semibold'}
        color={isPlaceholder ? '#637381' : '#008236'}
      >
        {value}
      </Text>
    </Flex>
  );
}

export function RegistrationSuccessBanner({
  successInfo,
}: {
  successInfo: RegistrationSuccessInfo;
}) {
  return (
    <Box
      bg="#F0FDF4"
      borderRadius="10px"
      px={6}
      pt={4}
      pb={4}
      data-testid="noo-buyer-registration-success"
    >
      <HStack align="start" spacing={3} mb={4}>
        <SuccessHomeIcon />
        <Text
          fontSize="xl"
          fontWeight="medium"
          lineHeight="28px"
          color="#008236"
        >
          Registrasi Rekanan Berhasil!
        </Text>
      </HStack>

      <Stack spacing={2} mb={4}>
        <SuccessDetailRow
          label="Nomor Pelanggan:"
          value={successInfo.customerNumber}
          isPlaceholder={successInfo.usesCustomerNumberPlaceholder}
        />
        <SuccessDetailRow
          label="Outlet Terdaftar:"
          value={String(successInfo.registeredOutletCount)}
        />
        <SuccessDetailRow
          label="Akun Sukanda OneLink"
          value={successInfo.accountStatusLabel}
        />
      </Stack>

      <Box
        as={NextLink}
        href="/login"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderRadius="10px"
        border="1px solid transparent"
        bg="#FFFFFF"
        px={4}
        py={4}
        sx={{ borderImage: `${NOO_NAV_GRADIENT} 1` }}
        data-testid="noo-buyer-start-shopping"
      >
        <Text fontSize="lg" fontWeight="medium" color="#373F41">
          Mulai Belanja Sekarang
        </Text>
        <Flex
          w="48px"
          h="48px"
          align="center"
          justify="center"
          borderRadius="8px"
          bg="#F1F8FF"
        >
          <ShoppingBagIcon />
        </Flex>
      </Box>
    </Box>
  );
}
export function NavigationLinksSection() {
  const homepageUrl = getPublicHomepageOrigin();

  return (
    <Stack spacing={3} data-testid="noo-buyer-status-navigation">
      <Button
        as={NextLink}
        href="/login"
        h="48px"
        w="full"
        borderRadius="8px"
        bg="#0941A8"
        color="#FFFFFF"
        fontSize="sm"
        fontWeight="medium"
        _hover={{ bg: '#083691' }}
        data-testid="noo-buyer-status-login-cta"
      >
        Masuk ke Akun
      </Button>
      <Button
        as={Link}
        href={homepageUrl}
        isExternal
        h="48px"
        w="full"
        borderRadius="8px"
        variant="outline"
        borderColor="#0941A8"
        color="#0941A8"
        bg="#FFFFFF"
        fontSize="sm"
        fontWeight="medium"
        _hover={{ bg: '#F1F8FF' }}
        data-testid="noo-buyer-status-homepage-cta"
      >
        Kembali ke Beranda
      </Button>
    </Stack>
  );
}
export function SupportSection({
  testId = 'noo-buyer-status-support',
}: {
  testId?: string;
}) {
  return (
    <Box
      border="1px solid"
      borderColor="#0000001A"
      bg="#FAFAFA"
      borderRadius="10px"
      px={4}
      py="13px"
    >
      <Text fontSize="md" fontWeight="medium" lineHeight="20px" color="#373F41">
        Butuh Bantuan?
      </Text>
      <Text mt={2} fontSize="sm" lineHeight="20px" color="#666666">
        Jika Anda memiliki pertanyaan tentang proses verifikasi, hubungi tim
        support kami.
      </Text>
      <Button
        as={Link}
        href={WHATSAPP_SUPPORT_URL}
        isExternal
        mt={4}
        h="48px"
        w="160px"
        borderRadius="8px"
        variant="outline"
        borderColor="#0941A8"
        color="#0941A8"
        bg="#FFFFFF"
        fontSize="sm"
        fontWeight="medium"
        _hover={{ bg: '#F1F8FF' }}
        data-testid={testId}
      >
        Hubungi Support
      </Button>
    </Box>
  );
}
export function DesktopAccountTabs({
  disablePasswordTab = true,
}: {
  disablePasswordTab?: boolean;
}) {
  const tabs: Array<{
    label: string;
    href: string;
    active?: boolean;
    disabled?: boolean;
    outlined?: boolean;
  }> = [
    {
      label: 'Pengajuan',
      href: '/noo-registration/status',
      active: true,
    },
    { label: 'Alamat', href: '/profile/addresses', disabled: true },
    {
      label: 'Info Pembayaran',
      href: '/profile/payment-info',
      disabled: true,
    },
    {
      label: 'Kata Sandi',
      href: '/profile/password',
      outlined: true,
      disabled: disablePasswordTab,
    },
  ];

  return (
    <HStack spacing={4} align="stretch" flexWrap="wrap" py={6} w="full">
      {tabs.map((tab) => {
        return (
          <Button
            key={tab.label}
            as={NextLink}
            href={tab.href}
            isDisabled={tab.disabled}
            pointerEvents={tab.disabled ? 'none' : 'auto'}
            tabIndex={tab.disabled ? -1 : 0}
            h="40px"
            px={4}
            borderRadius="6px"
            variant={tab.outlined ? 'outline' : 'solid'}
            bg={tab.active ? '#0941A8' : tab.outlined ? '#FFFFFF' : '#F2F2F7'}
            borderColor={tab.outlined ? '#0941A8' : '#00000000'}
            color={
              tab.active ? '#FFFFFF' : tab.disabled ? '#AEAEB2' : '#0941A8'
            }
            fontSize="sm"
            fontWeight="medium"
            _hover={{
              bg: tab.active ? '#083691' : tab.outlined ? '#F1F8FF' : '#F2F2F7',
            }}
            leftIcon={
              tab.active ? <ShoppingBagIcon color="#FFFFFF" /> : undefined
            }
            rightIcon={
              tab.active ? (
                <Icon as={ArrowCircleRightOutlined} boxSize={5} />
              ) : undefined
            }
            data-testid={`noo-buyer-status-tab-${tab.label
              .toLowerCase()
              .split(' ')
              .join('-')}`}
          >
            {tab.label}
          </Button>
        );
      })}
    </HStack>
  );
}
